import { useCallback, useState } from 'react';
import type { IsGroupCollapsed } from './nested-workflow-tree-to-flow';

/**
 * First nesting level drawn as a header alone.
 *
 * The workflow you opened and the ones it runs directly are the levels you came
 * to read, so they arrive expanded. Below that, a workflow's own steps are detail
 * you have not asked for yet, and every expanded box adds its full height to a
 * canvas that has to fit in a panel.
 */
export const NESTED_TREE_DEFAULT_EXPANDED_DEPTH = 2;

export type NestedTreeCollapse = {
  /** Passed to the layout pass; see {@link IsGroupCollapsed}. */
  readonly isCollapsed: IsGroupCollapsed;
  /** Fold or unfold one group. Callers pass the state they want, not a flip. */
  readonly setCollapsed: (key: string, collapsed: boolean) => void;
};

/**
 * Which groups in the tree are folded to a header.
 *
 * Stores only what the user has *changed*, and falls back to the depth rule for
 * everything else. Holding a set of collapsed keys instead would have to be
 * seeded once, and the tree is not all there when it first renders — levels
 * resolve one fetch at a time, so a level that arrived later would have missed
 * its default and unfolded itself. Deriving from depth means a level is folded
 * correctly the moment it appears, while an explicit toggle still wins for good.
 */
export function useNestedTreeCollapse(): NestedTreeCollapse {
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(
    new Map()
  );

  const isCollapsed = useCallback<IsGroupCollapsed>(
    (key, depth) =>
      overrides.get(key) ?? depth >= NESTED_TREE_DEFAULT_EXPANDED_DEPTH,
    [overrides]
  );

  const setCollapsed = useCallback((key: string, collapsed: boolean) => {
    setOverrides((previous) => new Map(previous).set(key, collapsed));
  }, []);

  return { isCollapsed, setCollapsed };
}
